import { apiService } from '@/lib/api-functions/api-service';
import { Company } from '@/types-games';
import { unifiedUserStore, UserContextData } from './unifiedUserStore';

/**
 * Update company information through the API
 */
export async function updateCompanyInfo(
  companyId: string,
  updates: Partial<Company>,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<Company> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Call the API to update the company
    const result = await apiService.updateCompany(companyId, {
      name: updates.name,
      subscription: updates.subscription,
      settings: updates.settings
    }); 
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update company');
    }

    // Convert API response to Company interface
    const updatedCompany: Company = {
      id: result.data.id,
      name: result.data.name,
      subscription: result.data.subscription,
      created_at: result.data.created_at,
      members: updates.members || [], // Keep existing members since API doesn't return them
      settings: result.data.settings
    };

    // Update the company in the userCompaniesGamesStore to keep the companies list in sync
    const currentData = unifiedUserStore.getData();
    const updatedData = currentData.map(row => 
      row.selectedCompanyId === companyId ? { ...row, companies: updatedCompany } : row
    );
    unifiedUserStore.setData(updatedData as UserContextData[]);

    return updatedCompany;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update company';
    console.error("updateCompanyInfo - error:", err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update company name specifically
 */
export async function updateCompanyName(
  companyId: string,
  newName: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<Company> {
  return updateCompanyInfo(companyId, { name: newName }, toast);
}
